import { ChevronDown } from "lucide-react";
import React from "react";

export interface PaginationProps {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  isLoading?: boolean;
  hasNext?: boolean;
  hasPrevious?: boolean;
  showPageSizeSelector?: boolean;
  pageSizeOptions?: number[];
  className?: string;
  itemLabel?: string;
}

/**
 * CPagination - Shared pagination component
 *
 * Displays pagination info, page size selector, and navigation controls.
 * Features:
 * - Page numbers with ellipsis for large page counts
 * - Previous/Next navigation buttons
 * - Configurable page size selector
 */
const CPagination: React.FC<PaginationProps> = ({
  currentPage,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  hasNext = true,
  hasPrevious = true,
  showPageSizeSelector = true,
  pageSizeOptions = [25, 50, 100, 250],
  className = "",
  itemLabel = "items",
}) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const uniquePageSizeOptions = React.useMemo(() => {
    const opts = new Set(pageSizeOptions);
    opts.add(pageSize);
    return Array.from(opts).sort((a, b) => a - b);
  }, [pageSizeOptions, pageSize]);

  const handlePreviousPage = () => {
    if (hasPrevious && currentPage > 1 && !isLoading) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNext && currentPage < totalPages && !isLoading) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (!isLoading && onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
  };

  if (totalCount === 0) {
    return null;
  }

  const isPrevDisabled = !hasPrevious || currentPage <= 1 || isLoading;
  const isNextDisabled = !hasNext || currentPage >= totalPages || isLoading;

  const getPageNumbers = (): (number | "...")[] => {
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | "...")[] = [];
    const halfVisible = Math.floor(maxVisible / 2);

    if (currentPage <= halfVisible + 1) {
      for (let i = 1; i <= maxVisible - 2; i++) {
        pages.push(i);
      }
      pages.push("...");
      pages.push(totalPages);
    } else if (currentPage >= totalPages - halfVisible) {
      pages.push(1);
      pages.push("...");
      for (let i = totalPages - maxVisible + 3; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        pages.push(i);
      }
      pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  // Button base styles
  const btnBase =
    "px-3 py-1.5 text-sm font-medium rounded-md border transition-colors";
  const btnEnabled =
    "text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer";
  const btnDisabled =
    "text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 opacity-50 cursor-default";

  // Page number button styles
  const pageBase =
    "min-w-[36px] px-2 py-1.5 text-sm rounded-md border transition-colors";
  const pageActive =
    "font-bold text-primary border-primary bg-primary/10 cursor-default";
  const pageInactive =
    "font-medium text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer";

  return (
    <div
      className={`flex flex-col sm:flex-row justify-between items-center gap-4 ${className}`}
    >
      {/* Left - Info */}
      <div className="text-sm text-slate-500 dark:text-slate-400">
        Showing{" "}
        <span className="font-medium text-slate-700 dark:text-slate-300">
          {startItem}-{endItem}
        </span>{" "}
        of{" "}
        <span className="font-medium text-slate-700 dark:text-slate-300">
          {totalCount}
        </span>{" "}
        {itemLabel}
      </div>

      {/* Right - Page size + Controls */}
      <div className="flex items-center gap-3">
        {/* Page size selector */}
        {showPageSizeSelector && onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 dark:text-slate-400">
              Rows:
            </span>
            <div className="relative">
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="pl-3 pr-8 py-1.5 text-sm font-medium rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer appearance-none"
                disabled={isLoading}
              >
                {uniquePageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            </div>
          </div>
        )}

        {/* Divider */}
        {showPageSizeSelector && onPageSizeChange && (
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-700" />
        )}

        {/* Pagination controls */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handlePreviousPage}
            disabled={isPrevDisabled}
            className={`${btnBase} ${isPrevDisabled ? btnDisabled : btnEnabled}`}
          >
            Previous
          </button>

          {pageNumbers.map((page, idx) =>
            page === "..." ? (
              <span
                key={`ellipsis-${idx === 1 ? "start" : "end"}`}
                className="px-2 py-1.5 text-sm text-slate-400 dark:text-slate-500"
              >
                ...
              </span>
            ) : (
              <button
                key={page}
                type="button"
                onClick={() => page !== currentPage && onPageChange(page)}
                disabled={isLoading || page === currentPage}
                className={`${pageBase} ${page === currentPage ? pageActive : pageInactive}`}
              >
                {page}
              </button>
            ),
          )}

          <button
            type="button"
            onClick={handleNextPage}
            disabled={isNextDisabled}
            className={`${btnBase} ${isNextDisabled ? btnDisabled : btnEnabled}`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default CPagination;
